import { apiHelpers } from '../utils/api';
import {
  Job,
  CreateJobData,
  UpdateJobData,
  JobsResponse,
  JobFilters,
  JobStats,
  ConvertBillsToInvoiceData
} from '../types/job';

export const jobService = {
  async getJobs(filters: JobFilters = {}): Promise<JobsResponse> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.customerId) params.append('customerId', filters.customerId);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    return await apiHelpers.get(`/jobs?${params.toString()}`);
  },

  async getJob(id: string): Promise<Job> {
    return await apiHelpers.get(`/jobs/${id}`);
  },

  async createJob(data: CreateJobData): Promise<Job> {
    // Remove jobNumber from data since it will be auto-generated by backend
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { jobNumber, ...jobDataWithoutNumber } = data;
    return await apiHelpers.post('/jobs', jobDataWithoutNumber);
  },

  async updateJob(id: string, data: UpdateJobData): Promise<Job> {
    return await apiHelpers.put(`/jobs/${id}`, data);
  },

  async deleteJob(id: string): Promise<void> {
    return await apiHelpers.delete(`/jobs/${id}`);
  },

  async getJobStats(): Promise<JobStats> {
    return await apiHelpers.get('/jobs/stats');
  },

  async convertBillsToInvoice(jobId: string, data: ConvertBillsToInvoiceData): Promise<any> {
    return await apiHelpers.post(`/jobs/${jobId}/convert-to-invoice`, data);
  },

  // Helper function to generate unique job number
  generateJobNumber(customerId: string, sequence?: number): string {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const seq = String(sequence || 1).padStart(3, '0');
    return `JOB${year}${month}${seq}`;
  },

  // Helper function to validate job data
  validateJobData(data: CreateJobData): string[] {
    const errors: string[] = [];

    if (!data.jobNumber || data.jobNumber.trim().length === 0) {
      errors.push('Job number is required');
    }

    if (!data.jobName || data.jobName.trim().length === 0) {
      errors.push('Job name is required');
    }

    if (!data.customerId) {
      errors.push('Customer is required');
    }

    if (data.estimatedValue !== undefined && data.estimatedValue < 0) {
      errors.push('Estimated value cannot be negative');
    }

    if (data.startDate && data.endDate) {
      const start = new Date(data.startDate);
      const end = new Date(data.endDate);
      if (start > end) {
        errors.push('Start date cannot be after end date');
      }
    }

    return errors;
  },

  // Helper function to format job status display
  formatStatus(status: string): string {
    const statusMap: Record<string, string> = {
      'ACTIVE': 'Active',
      'COMPLETED': 'Completed',
      'ON_HOLD': 'On Hold',
      'CANCELLED': 'Cancelled'
    };
    return statusMap[status] || status;
  },

  // Helper function to get status color
  getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'ACTIVE': 'bg-green-100 text-green-800',
      'COMPLETED': 'bg-blue-100 text-blue-800',
      'ON_HOLD': 'bg-yellow-100 text-yellow-800',
      'CANCELLED': 'bg-red-100 text-red-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  },

  // Helper function to calculate job progress
  calculateProgress(job: Job): number {
    if (job.status === 'COMPLETED') return 100;
    if (job.status === 'CANCELLED') return 0;
    if (!job.estimatedValue || job.estimatedValue === 0) return 0;
    
    return Math.min(100, (job.actualValue / job.estimatedValue) * 100);
  }
};